import {
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Insights as InsightsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Button,
  Grid,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Breadcrumb } from 'src/components/Breadcrumb';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { CopyToClipboardButton } from 'src/components/CopyToClipboardButton';
import GridData from 'src/components/GridData/GridData';
import { Dropdown, mutedGridRowClassName } from 'src/UIKit';
import { InlineChip } from './InlineChip.styles';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import { getDateTimeUSAFormat } from 'src/constants/helpers';
import { getUsedCampaignConfigs } from 'src/modules/campaignConfigBuilder/slices';
import { useUsedCampaignConfigLoader } from 'src/modules/campaignConfigBuilder/hooks/useUsedCampaignConfigLoader';
import { useCampaignsViewController, useContactCaptureOfferController } from '../../controllers';
import { formatTypeName, toCampaignCreate, toCampaignEdit } from '../../helpers';
import { CampaignListItem } from '../../types';
import { CampaignAnalyticsModal } from '../CampaignAnalyticsModal';
import { CampaignTypeSelectionDialog } from '../CampaignTypeSelectionDialog';
import { MessageTypeChips } from '../MessageTypeChips';
import { ContactCaptureOfferDialog } from '../ContactCaptureOfferDialog/ContactCaptureOfferDialog';

export const CampaignsView = () => {
  const navigate = useNavigate();
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignListItem | null>(null);
  const {
    isPageLoading,
    campaignsFilter,
    campaignList,
    company,
    changeCampaignFilterForm,
    changeCampaignsSearchFilter,
    changeCampaignsSorting,
    changeCampaignsPagination,
    activateCampaign,
    deactivateCampaign,
    campaignTypeSelectionDialogController,
  } = useCampaignsViewController();
  const contactCaptureOffer = useContactCaptureOfferController();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const { loadUsedCampaignConfigs } = useUsedCampaignConfigLoader();
  const usedConfigs = useSelector(getUsedCampaignConfigs);

  const typeFilterOptions = useMemo(
    () => [
      { value: 'all', title: 'All' },
      ...usedConfigs.map(c => ({ value: c.id, title: c.config.name })).sort((a, b) => a.title.localeCompare(b.title)),
    ],
    [usedConfigs],
  );

  useEffect(() => {
    loadUsedCampaignConfigs();
  }, [loadUsedCampaignConfigs]);

  const handleViewAnalytics = (campaign: CampaignListItem) => {
    setSelectedCampaign(campaign);
    setIsAnalyticsModalOpen(true);
  };

  return (
    <>
      <ConfirmationDialog />

      <CampaignTypeSelectionDialog {...campaignTypeSelectionDialogController} />
      <ContactCaptureOfferDialog
        offer={contactCaptureOffer.offer}
        open={contactCaptureOffer.isOpen}
        onAccept={contactCaptureOffer.accept}
        onClose={contactCaptureOffer.dismiss}
      />

      <CampaignAnalyticsModal
        open={isAnalyticsModalOpen}
        onClose={() => {
          setIsAnalyticsModalOpen(false);
          setSelectedCampaign(null);
        }}
        campaign={selectedCampaign}
      />

      <>
        <PrivateLayoutView
          title={
            <Breadcrumb items={[{ label: 'Campaigns', isActive: true }]} />
          }
        >
          <GridData
            header={
              <TableHeader
                searchValue={campaignsFilter.query}
                onSearchChange={changeCampaignsSearchFilter}
                filters={
                  <>
                    <TableHeaderItem>
                      <Dropdown
                        label="Type"
                        value={campaignsFilter.configId ?? 'all'}
                        options={typeFilterOptions}
                        onChange={(_, v) =>
                          changeCampaignFilterForm({ configId: (v as string) || undefined })
                        }
                        margin="none"
                      />
                    </TableHeaderItem>

                    <TableHeaderItem>
                      <Dropdown
                        label="Status"
                        value={campaignsFilter.status}
                        options={[
                          { title: 'All', value: 'all' },
                          { title: 'Active', value: 'active' },
                          { title: 'Inactive', value: 'inactive' },
                          { title: 'Draft', value: 'draft' },
                        ]}
                        onChange={(_, v) => changeCampaignFilterForm({ status: v })}
                        margin="none"
                      />
                    </TableHeaderItem>
                  </>
                }
                actions={
                  <Button
                    onClick={() => campaignTypeSelectionDialogController.open()}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    Create Campaign
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={campaignList?.items}
            rowHeight={75}
            columns={[
              {
                field: 'name',
                headerName: 'Campaign',
                renderCell: r => {
                  const statusLabel =
                    r.row.status === 'active'
                      ? null
                      : r.row.status === 'draft'
                        ? 'Draft'
                        : 'Inactive';

                  return (
                    <Grid container spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
                      <Grid item xs={12} zeroMinWidth>
                        <Grid
                          container
                          spacing={0.5}
                          wrap="nowrap"
                          sx={{ alignItems: 'center', minWidth: 0 }}
                        >
                          <Grid item zeroMinWidth sx={{ flex: '0 1 auto', minWidth: 0 }}>
                            <Tooltip placement="top" title={r.row.name} arrow>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ display: 'block', maxWidth: '100%' }}
                              >
                                {r.row.name}
                              </Typography>
                            </Tooltip>
                          </Grid>

                          {statusLabel && (
                            <Grid item sx={{ flex: '0 0 auto' }}>
                              <InlineChip
                                color="default"
                                label={statusLabel}
                                size="small"
                                variant="outlined"
                              />
                            </Grid>
                          )}

                          <Grid item sx={{ flex: '0 0 auto' }}>
                            <MessageTypeChips messageTypes={r.row.scheduledMessages.map(m => m.type)} />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} zeroMinWidth>
                        <CopyToClipboardButton text={r.row.id}>
                          <Typography variant="caption">{r.row.id}</Typography>
                        </CopyToClipboardButton>
                      </Grid>
                    </Grid>
                  );
                },
                display: 'flex',
                minWidth: 350,
                editable: false,
                sortable: true,
                flex: 2,
              },
              {
                field: 'type',
                headerName: 'Type',
                renderCell: r => {
                  return usedConfigs.find(c => c.id === r.row.configId)?.config.name ||
                    formatTypeName(r.row.type, r.row.externalType);
                },
                minWidth: 150,
                editable: false,
                sortable: false,
                flex: 1,
              },
              {
                field: 'createdAt',
                headerName: 'Created At',
                renderCell: r => {
                  return getDateTimeUSAFormat(new Date(r.row.createdAt));
                },
                minWidth: 100,
                editable: false,
                sortable: true,
                flex: 1,
              },
              {
                field: 'updatedAt',
                headerName: 'Updated At',
                renderCell: r => {
                  return getDateTimeUSAFormat(new Date(r.row.updatedAt));
                },
                minWidth: 100,
                editable: false,
                sortable: true,
                flex: 1,
              },
            ]}
            renderAction={r => {
              const isOnlyReadable = r.isEditable === false;
              const isDraftCampaign = r.status === 'draft';
              const isInactiveCampaign = r.status === 'inactive' || isDraftCampaign;
              return (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      toCampaignEdit(navigate, r);
                    }}
                  >
                    {isOnlyReadable && !isDraftCampaign ? (
                      <VisibilityIcon fontSize="inherit" />
                    ) : (
                      <EditIcon fontSize="inherit" />
                    )}
                  </IconButton>

                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => toCampaignCreate(navigate, r)}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>

                  <IconButton
                    size="small"
                    color="primary"
                    disabled={isDraftCampaign}
                    onClick={() => handleViewAnalytics(r)}
                  >
                    <InsightsIcon fontSize="inherit" />
                  </IconButton>

                  <Switch
                    size="small"
                    checked={!isInactiveCampaign}
                    disabled={isOnlyReadable || isDraftCampaign}
                    onChange={() => {
                      if (!isInactiveCampaign) {
                        openDialog({
                          title: `Deactivate ${r.name}`,
                          content: 'Are you sure you want to deactivate this campaign?',
                          onConfirm: () => deactivateCampaign(r.id),
                        });
                      }

                      if (isInactiveCampaign) {
                        if (company?.subscription?.status !== 'active') {
                          openDialog({
                            title: 'Subscription Required',
                            content: 'An active subscription is required to activate campaigns.',
                            onConfirm: () => {},
                            confirmButtonText: 'OK',
                            confirmButtonColor: 'primary',
                            cancelButtonVariant: 'hidden'
                          });
                        } else {
                          openDialog({
                            title: `Activate ${r.name}`,
                            content:
                              'Are you sure you want to activate this campaign? If keywords in this campaign are already part of another active campaign, this may cause a bad experience for the recipient. Are you sure you want to continue?',
                            onConfirm: () => activateCampaign(r.id),
                          });
                        }
                      }
                    }}
                  />
                </>
              );
            }}
            getRowClassName={params =>
              params.row.status === 'inactive' ? mutedGridRowClassName : ''
            }
            sorting={{
              value: campaignsFilter.sort,
              onChange: changeCampaignsSorting,
            }}
            pagination={{
              page: campaignsFilter.page,
              pageSize: campaignsFilter.limit,
              totalCount: campaignList?.count,
              onChange: changeCampaignsPagination,
            }}
          />
        </PrivateLayoutView>
      </>
    </>
  );
};
